import './DatasetEdit.scss';

import React, { memo, useEffect, useState } from 'react'
import { Helmet } from "react-helmet";
import { Badge, Breadcrumb, Button, Card, Col, Container, Form, Row, Table } from 'react-bootstrap'
import { Link, useNavigate, useParams } from 'react-router-dom';
import { BdaDataset } from '../../models/BdaDataset';
import { Datasets } from '../../api/Datasets';
import AppLoader from '../../components/common/AppLoader';

import { Datasources } from '../../api/Datasources';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { VizClient } from '../../api/VizClient';
import classNames from 'classnames';
import IconButton from '../../components/common/IconButton';
import { useToasts } from '../../contexts/toasts';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { getConnectionDetails, createQueryDetails } from '../../utils';
import Tree, { TreeElement } from '../../components/generics/Tree';
import { cloneDeep, set } from 'lodash';
import { Alert, Select } from 'antd';
import UserItem from '../../components/common/UserItem';
import AppSearch from '../../components/common/AppSearch';
import { useRef } from 'react';
import { useCallback } from 'react';
import { Datasource } from '../../models/Datasource';

const columnTypes = [
  { label: 'String', value: 'String' },
  { label: 'Varchar', value: 'Varchar' },
  { label: 'Number', value: 'Number' },
  { label: 'Int', value: 'Int' },
  { label: 'Boolean', value: 'Boolean' },
  { label: 'Double', value: 'Double' },
  { label: 'Float', value: 'Float' },
  { label: 'Date', value: 'Date' },
];

export default function DatasetEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  const [validated, setValidated] = useState(false);
  const [dataset, setDataset] = useState<BdaDataset>();
  const [searchText, setSearchText] = useState("");
  const [datasources, setDatasources] = useState<Datasource[]>();
  const [selectedDatasource, setSelectedDatasource] = useState<Datasource>();
  const [selectedPath, setSelectedPath] = useState<string>();
  const [type, setType] = useState("table");
  const [directoryTree, setDirectoryTree] = useState<TreeElement[]>();
  const [tables, setTables] = useState<any[]>();
  const [selectedTable, setSelectedTable] = useState<any>();
  const [columns, setColumns] = useState<any[]>();
  const [originalColumns, setOriginalColumns] = useState<any[]>();
  const [editing, setEditing] = useState<number>();
  const [editingColumn, setEditingColumn] = useState<any>();
  const [datasetSettingsError, setDatasetSettingsError] = useState(false);
  const [error, setError] = useState(false);

  const treeContainer = useRef<HTMLDivElement | null>(null);
  const datasourceContainer = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (id) retrieveDataset();
    else setDataset(new BdaDataset());

    initDatasources();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setDirectoryTree(undefined);
    if (selectedDatasource) {
      if (selectedDatasource.type === "objectStorage") getDirectoryTree();
      else getTables();
    }
    // eslint-disable-next-line
  }, [selectedDatasource]);

  useEffect(() => {
    if (id && datasources && selectedDatasource) {
      const selectedDatasource = document.querySelector(".list-wrapper div.datasource-list-item.selected") as HTMLButtonElement;
      if (selectedDatasource) datasourceContainer.current!.scrollTop = selectedDatasource!.offsetTop - datasourceContainer.current!.offsetTop;
    }
  }, [id, datasources, selectedDatasource]);

  useEffect(() => {
    if (id && selectedPath && directoryTree) {
      const selectedBranch = document.querySelector(".tree-container button.accordion-toggle.selected") as HTMLButtonElement;
      if (selectedBranch) treeContainer.current!.scrollTop = selectedBranch!.offsetTop - treeContainer.current!.offsetTop;
    }
  }, [id, selectedPath, directoryTree]);

  useEffect(() => {
    if (selectedTable && selectedDatasource?.type !== 'messageBroker') getDatasetColumns();
    // eslint-disable-next-line
  }, [selectedTable]);

  const retrieveDataset = () => {
    Datasets.getDataset(+id!)
      .then(
        ({ data: dataset }) => {
          setDataset({ ...dataset, path: dataset.datasetFileType!.tableName });
          setSelectedDatasource(dataset.datasource);

          if (dataset.datasetFileType!.type === "table") {
            const array = dataset.datasetFileType!.tableName.split("/");
            const tableName = array[array.length - 1];
            setSelectedTable({ name: tableName });

            setColumns(dataset.datasetFileType!.columns);
            setSelectedPath(dataset.datasetFileType!.tableName + "/");
          }
        },
        (err) => {
          setError(true);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('datasets-error', message, `Datasets Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const initDatasources = () => {
    Datasources.getDatasources().then(
      ({ data: datasources }) => setDatasources(datasources),
      (err) => {
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        showToast('datasources-error', message, `Datasources Error ${status ? `(${status})` : ''}`, 'danger');
      }
    )
  }

  const handleSearchChange = (evt: React.ChangeEvent<HTMLInputElement>) => setSearchText(evt.target.value);

  const revertEdit = (index: number) => () => {
    const newColumns = cloneDeep(columns!);
    newColumns[index].edited = false;
    newColumns[index] = originalColumns![index];
    setColumns(newColumns);
  }

  const editColumn = (index: number) => () => {
    setEditing(index);
    setEditingColumn(cloneDeep(columns![index]));
  }

  const cancelEdit = () => {
    setEditing(undefined);
    setEditingColumn(null);
  }

  const confirmEdit = () => {
    const newColumns = cloneDeep(columns!);
    newColumns[editing!] = editingColumn;
    if (editingColumn.type !== originalColumns![editing!].type) newColumns[editing!].edited = true;
    else newColumns[editing!].edited = false;

    setColumns(newColumns);
    cancelEdit();
  }
  const onCancel = () => navigate('/datasets');

  const getTables = () => {
    const connectionDetails = getConnectionDetails(selectedDatasource!);
    const body = createQueryDetails(connectionDetails);
    const isMessageBroker = selectedDatasource!.type === 'messageBroker';

    const promise = isMessageBroker
      ? VizClient.getTopics(body, selectedDatasource!.driverType)
      : VizClient.getTables(body, selectedDatasource!.driverType);

    promise.then(
      ({ data: res }) => {
        if (!res) {
          showToast('tables-error', `Error while retrieving ${isMessageBroker ? 'topics' : 'tables'}`, `${isMessageBroker ? 'Topics' : 'Tables'} Error"`, 'danger');
          setTables([]);
          setDatasetSettingsError(true);
          return;
        }

        if (isMessageBroker) {
          const tables = res.topics?.map((t: string) => ({ name: t })) || [];
          setTables(tables);
        }
        else setTables(res.tables || []);
      },
      (err) => {
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        showToast('tables-error', message, `${isMessageBroker ? 'Topics' : 'Tables'} Error ${status ? `(${status})` : ''}`, 'danger');
        setDatasetSettingsError(true);
      },
    )
  }

  const getDirectoryTree = useCallback((path?: string, jsonPath?: string) => {
    const connectionDetails = getConnectionDetails(selectedDatasource!);
    connectionDetails.path = path || connectionDetails.prefixPath || "";
    if (connectionDetails.prefixPath) delete connectionDetails.prefixPath;
    const body = createQueryDetails(connectionDetails);

    VizClient.browseDirectories(body, selectedDatasource!.driverType)
      .then(
        ({ data: res }) => {
          if (!res) {
            showToast('directories-error', "Error while retrieving directories", "Browsing Error", 'danger');
            setDirectoryTree([]);
            return;
          }
          res.objects.forEach((obj: any) => {
            obj.path = connectionDetails.path + obj.name;
            obj.selected = false;
          });
          if (path) {
            setDirectoryTree(tree => {
              const newTree = cloneDeep(tree!);
              set(newTree, jsonPath!, res.objects);
              return newTree;
            });
          }
          else setDirectoryTree(res.objects);
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('directories-error', message, `Browsing Error ${status ? `(${status})` : ''}`, 'danger');
          setDatasetSettingsError(true);
        }
      )
    // eslint-disable-next-line
  }, [selectedDatasource]);

  const handleBranchShow = useCallback((path: string, jsonPath: string, browse: boolean) => {
    if (browse) getDirectoryTree(path, jsonPath);
  }, [getDirectoryTree]);

  const handleBranchSelect = useCallback((path: string, _jsonPath: string, isTable?: boolean) => {
    setSelectedPath(path);
    setDataset(ds => ({
      ...ds!,
      path
    }));

    if (isTable) {
      setColumns(undefined);
      const name = path.split("/").at(-2);
      const prefixPath = path.split("/").slice(0, -2).join("/");
      setSelectedTable({ name, prefixPath });
    }
  }, []);

  const handleFieldChange = (field: keyof BdaDataset) => (evt: any) => {
    const value = field === "tags" || field === "accessLevel" ? evt : evt.target.value;
    setDataset({
      ...dataset!,
      [field]: value,
    });
  }

  const handleTypeChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const value = evt.target.value;
    setType(value);
  }

  const handleColumnTypeChange = (value: string) => {
    setEditingColumn({
      ...editingColumn,
      type: value,
    });
  }

  const getDatasetColumns = () => {
    const connectionDetails = getConnectionDetails(selectedDatasource!, selectedTable.name, dataset?.datasetFileType);
    if (selectedTable.prefixPath !== undefined) connectionDetails.prefixPath = selectedTable.prefixPath + "/";
    if (connectionDetails.table.endsWith("/")) connectionDetails.table = connectionDetails.table.slice(0, -1);

    const body = createQueryDetails(connectionDetails);

    VizClient.getColumns(body, selectedDatasource!.driverType)
      .then(
        ({ data: res }) => {
          if (!res) {
            showToast('columns-error', "Error while retrieving columns", "Columns Error", 'danger');
            setColumns([]);
            setOriginalColumns([]);
            return;
          }

          setColumns(res ? res.columns : [])
          setOriginalColumns(res ? res.columns : []);
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('columns-error', message, `Columns Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const submitDataset = (evt: React.FormEvent<HTMLFormElement>) => {
    const form = evt.currentTarget;
    evt.preventDefault();
    evt.stopPropagation();
    setValidated(true);

    if (!selectedDatasource || (type === "table" && !selectedTable)) return;

    if (form.checkValidity() === true) {
      // console.log(type, selectedDatasource, dataset, selectedTable);
      const newDataset = {
        id,
        name: dataset!.name,
        description: dataset!.description,
        datasetFileType: {
          type,
          columns,
          tableName: selectedDatasource?.type === "objectStorage" ? dataset!.path : selectedTable.name,
          path: type === "image" ? dataset!.path : null,
        },
        datasource: selectedDatasource,
        // infoConnection: string,
        accessLevel: dataset!.accessLevel,
        tags: dataset!.tags,
      };
      if (newDataset.datasetFileType.tableName?.endsWith("/")) newDataset.datasetFileType.tableName = newDataset.datasetFileType.tableName.slice(0, -1);

      // console.log(newDataset); return;
      showLoader();

      const promise = id ? Datasets.updateDataset(newDataset as BdaDataset) : Datasets.createDataset(newDataset as BdaDataset);

      promise
        .then(
          ({ data: newDataset }) => {
            hideLoader();
            showToast('create-dataset-success', `Dataset ${id ? 'edited' : 'registered'} successfully.`, 'Dataset Success', 'success');
            navigate(`/datasets/${newDataset.id}`);
          },
          () => {
            hideLoader();
            showToast('create-dataset-error', `Could not ${id ? 'edit' : 'register'} dataset.`, 'Dataset Error', 'danger');
          },
        );
    }
  }

  const selectDatasource = (datasource: Datasource) => () => {
    setDatasetSettingsError(false);
    setTables(undefined);
    setSelectedTable(null);
    setSelectedDatasource(datasource);
  }

  const selectTable = (tableName: string) => {
    setEditing(undefined);
    setColumns(undefined);
    const table = tables!.find(table => table.name === tableName);
    setSelectedTable(table);
  }

  const renderConnectionSettings = () => {
    return (
      <Row>
        <Col lg={3} className="border-end">
          <Link to="/datasources" target="_blank" rel="noopener noreferrer">
            <h6>
              <span className="me-2">Data Source</span>
              <FontAwesomeIcon icon={solid('external-link')} />
            </h6>
          </Link>

          <div className="pb-2 border-bottom">
            <AppSearch value={searchText} onChange={handleSearchChange} />
          </div>

          <div className="list-wrapper border-bottom" ref={datasourceContainer}>
            {datasources
              ? datasources
                .filter(datasource => datasource.name.toLowerCase().includes(searchText.toLowerCase()))
                .map(datasource => (
                  <DatasourceListItem key={datasource.id} datasource={datasource} onClick={selectDatasource(datasource)} selected={selectedDatasource?.id === datasource.id} />
                ))
              : (
                <div className="d-flex py-5 justify-content-center">
                  <AppLoader size={100} />
                </div>
              )
            }
          </div>
        </Col>
        <Col>{renderSettingsContent()}</Col>
      </Row>
    )
  }

  const renderSettingsContent = () => {
    if (!selectedDatasource) {
      return (
        <div className="rounded border bg-light p-5 d-flex flex-column align-items-center justify-content-center text-muted">
          <FontAwesomeIcon icon={solid('faucet-drip')} size="2x" />
          <h5 className="mb-0">Choose data source to see details here.</h5>
        </div>
      )
    }

    if (datasetSettingsError) return (
      <Alert
        showIcon
        type="error"
        message="An error occured with the selected data source. Try again later"
      />
    );

    return (
      <>
        {selectedDatasource?.type !== 'messageBroker' && (
          <Form.Group className="d-flex justify-content-between align-items-center mb-3">
            <Form.Label>Dataset Type</Form.Label>

            <div>
              <Form.Check type="radio" id="dataset-type-table" label="Table" name="dataset-type" checked={type === "table"} value="table" onChange={handleTypeChange} inline />
              <Form.Check type="radio" id="dataset-type-image" label="Other" name="dataset-type" checked={type === "image"} value="image" onChange={handleTypeChange} inline />
            </div>
          </Form.Group>
        )}

        {selectedDatasource.type === "objectStorage" && renderDirectoryTree()}

        {selectedDatasource.type !== "objectStorage" && (
          <>
            <div>
              <Form.Label>{selectedDatasource?.type === 'messageBroker' ? 'Topic' : 'Table'}</Form.Label>
              <Select
                showSearch
                allowClear
                loading={!tables}
                className="w-100"
                placeholder={`Choose ${selectedDatasource?.type === 'messageBroker' ? 'Topic' : 'Table'}`}
                status={(validated && !selectedTable) ? 'error' : undefined}
                optionFilterProp="label"
                onChange={selectTable}
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={tables?.map(table => ({
                  label: table.name,
                  value: table.name,
                }))}
                value={selectedTable?.name}
              />
            </div>
          </>
        )}
      </>
    )
  }

  const renderDirectoryTree = useCallback(() => {
    return (
      <>
        <Form.Group controlId="dataset-path" className="mb-2 align-items-center" as={Row}>
          <Col md={3}>
            <Form.Label>Selected Directory Path</Form.Label>
          </Col>
          <Col>
            <Form.Control disabled size="sm" placeholder="Select directory from tree below" value={dataset!.path} />
          </Col>
        </Form.Group>

        <div className="tree-container border" ref={treeContainer}>
          <Tree elements={directoryTree} onBranchShow={handleBranchShow} onSelect={handleBranchSelect} selectedPath={selectedPath} />
        </div>
      </>
    )
  }, [directoryTree, selectedPath, handleBranchShow, handleBranchSelect, dataset]);

  const renderTableSchema = () => {
    if (!columns) return (
      <div className="p-5 text-center">
        <AppLoader size={300} />
      </div>
    );

    return (
      <div className="mt-3">
        <h6 className="mb-2">Table Schema</h6>
        <div className="list-wrapper">
          <Table className="table-schema" bordered>
            <thead className="bg-light">
              <tr>
                <th>Name</th>
                <th className="text-center" style={{ width: "40%" }}>Type</th>
                <th style={{ width: "100px" }}></th>
              </tr>
            </thead>
            <tbody>
              {columns.map((col, index) => (
                <tr key={col.name} className={classNames({ edit: editing === index, edited: col.edited })}>
                  <td>{col.name}</td>
                  <td className="text-center">
                    {editing !== index && <small className="text-muted text-uppercase">{col.type}</small>}
                    {editing === index && (
                      <Select
                        placeholder="Select column type"
                        className="w-100"
                        onChange={handleColumnTypeChange}
                        options={columnTypes}
                        value={editingColumn.type}
                      />
                    )}
                  </td>
                  <td className="text-center">
                    {editing !== index && (
                      <>
                        {col.edited && (
                          <IconButton type="button" className="p-1" onClick={revertEdit(index)} tooltip="Revert">
                            <FontAwesomeIcon icon={solid('undo')} />
                          </IconButton>
                        )}
                        <IconButton type="button" className="p-1" onClick={editColumn(index)} tooltip="Edit">
                          <FontAwesomeIcon icon={solid('edit')} />
                        </IconButton>
                      </>
                    )}
                    {editing === index && (
                      <>
                        <IconButton type="button" className="text-success p-1 me-2" onClick={confirmEdit} tooltip="Confirm">
                          <FontAwesomeIcon icon={solid('check')} />
                        </IconButton>
                        <IconButton type="button" className="text-danger p-1" onClick={cancelEdit} tooltip="Cancel">
                          <FontAwesomeIcon icon={solid('times')} />
                        </IconButton>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>
    )
  }

  const renderDatasetForm = () => {
    return (
      <Form noValidate validated={validated} onSubmit={submitDataset}>
        <Card.Body>
          <Row className="mb-3">
            <Col>
              <Form.Group controlId="dataset-name" className="mb-2">
                <Form.Label>Name</Form.Label>
                <Form.Control size="sm" placeholder="Dataset Name *" value={dataset!.name} onChange={handleFieldChange('name')} required />
                <Form.Control.Feedback type="invalid">Dataset name is required.</Form.Control.Feedback>
              </Form.Group>

              <Form.Group controlId="dataset-tags">
                <Form.Label>Tags</Form.Label>
                <Select mode="tags" className="w-100" placeholder="Dataset Tags" value={dataset!.tags} onChange={handleFieldChange('tags')} />
              </Form.Group>
            </Col>

            <Col>
              <Form.Group controlId="dataset-description">
                <Form.Label>Description</Form.Label>
                <Form.Control as="textarea" size="sm" rows={4} placeholder="Dataset description" value={dataset!.description} onChange={handleFieldChange('description')} />
              </Form.Group>
            </Col>
          </Row>

          {renderConnectionSettings()}

          {selectedTable && selectedDatasource?.type !== 'messageBroker' && renderTableSchema()}

          {validated && (!selectedDatasource || (type === "table" && !selectedTable)) && (
            <Form.Control.Feedback type="invalid" className="d-block">You need to select a database and a table to register a dataset.</Form.Control.Feedback>
          )}
        </Card.Body>

        <div className="d-flex border-top p-2 px-3 justify-content-between">
          <Button type="button" size="sm" variant="light" className="border" onClick={onCancel}>
            <FontAwesomeIcon icon={solid('chevron-left')} />
            <span className="ms-2">Cancel</span>
          </Button>
          <Button type="submit" size="sm" variant="success">
            <FontAwesomeIcon icon={solid('save')} />
            <span className="ms-2">Save</span>
          </Button>
        </div>
      </Form>
    )
  }

  const renderAsyncContent = () => {
    if (error) return (
      <Alert
        type="error"
        showIcon
        message="An error occured while retrieving dataset. Try again later."
      />
    );

    return dataset
      ? renderDatasetForm()
      : (
        <div className="p-5 text-center">
          <AppLoader />
        </div>
      );
  }

  return (
    <Container className="my-3">
      <Helmet>
        <title>Dataset {id ? `${id} Edit` : "Registration"}</title>
      </Helmet>

      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/datasets' }}>Datasets</Breadcrumb.Item>
        <Breadcrumb.Item active>{id ? dataset?.name + ' Edit' : 'New Dataset'}</Breadcrumb.Item>
      </Breadcrumb>

      <Card>
        <Row className="gx-0 border-bottom p-2 px-3">
          <h5 className="mb-2">{id ? 'Edit' : 'New'} Dataset</h5>
          <h6 className="text-muted mb-0">{id ? 'Edit existing dataset fields.' : 'Fill in desired dataset details.'}</h6>
        </Row>

        {renderAsyncContent()}
      </Card>
    </Container>
  )
}

type Props = {
  datasource: Datasource;
  selected?: boolean;
  onClick: React.MouseEventHandler<HTMLDivElement>;
}

const DatasourceListItem = memo(({ datasource, selected, onClick }: Props) => {
  return (
    <Card className={classNames("datasource-list-item", { selected })} onClick={onClick}>
      <Row className="gx-0 align-items-center mb-2">
        <Col className="overflow-hidden pe-2">
          <h6 className="mb-0 text-truncate">{datasource.name}</h6>
        </Col>

        {selected && (
          <Col xs={1}>
            <FontAwesomeIcon icon={solid('check')} size="lg" className="text-success" />
          </Col>
        )}
      </Row>

      <div className="d-flex justify-content-between align-items-center">
        <UserItem user={datasource.owner!} organization={datasource.organization!} className="sm" />
        <Badge bg="primary" className="w-auto">{datasource.accessLevel}</Badge>
      </div>
    </Card>
  )
});